<template>
  <div>
    <base-form :title="$t('resume.details.title')"
               :loading="loading"
               @submit="onFormSubmit">
      <div class="flex">
        <fg-input :placeholder="$t('resume.position')"
                  :name="$t('resume.position')"
                  :label="$t('resume.position')"
                  v-model="resumeToEdit.position"
                  class="w-full md:w-1/2 mr-2">
        </fg-input>
        <fg-input :placeholder="$t('resume.info')"
                  :name="$t('resume.info')"
                  :label="$t('resume.info')"
                  v-model="resumeToEdit.info"
                  class="w-full md:w-1/2">
        </fg-input>
      </div>
      <div class="flex">
        <fg-input :placeholder="$t('generic.address')"
                  :name="$t('generic.address')"
                  :label="$t('generic.address')"
                  v-model="resumeToEdit.address"
                  class="w-full md:w-1/2 mr-2">
        </fg-input>
        <fg-input :placeholder="$t('generic.postalCode')"
                  :name="$t('generic.postalCode')"
                  :label="$t('generic.postalCode')"
                  v-model="resumeToEdit.postal_code"
                  class="w-full md:w-1/2">
        </fg-input>
      </div>
      <div class="flex">
        <fg-input :placeholder="$t('generic.city')"
                  :name="$t('generic.city')"
                  :label="$t('generic.city')"
                  v-model="resumeToEdit.city"
                  class="w-full md:w-1/2 mr-2">
        </fg-input>
        <fg-input :label="$t('generic.country')"
                  :error="getError($t('generic.country'))"
                  class="w-full md:w-1/2">
          <country-autocomplete v-model="resumeToEdit.country_code"
                                :name="$t('generic.country')">
          </country-autocomplete>
        </fg-input>
      </div>
      <div class="flex">
        <fg-input :label="$t('generic.phone')" class="w-full">
          <phone-input :name="$t('generic.phone')"
                       v-model="resumeToEdit.phone_number">
          </phone-input>
        </fg-input>
      </div>
      <div class="flex">
        <fg-input :placeholder="$t('generic.salary')"
                  :name="$t('generic.salary')"
                  :label="$t('generic.salary')"
                  v-model="resumeToEdit.salary_min"
                  type="number"
                  class="w-full md:w-1/2 mr-2">
          <template v-slot:addonRight>
            <small>{{currencySymbol}}</small>
          </template>
        </fg-input>
        <fg-input :label="$t('generic.salaryType')" class="w-full md:w-1/2">
          <salary-type-select v-model="resumeToEdit.salary_type" :name="$t('generic.salaryType')">
          </salary-type-select>
        </fg-input>
      </div>
      <div class="flex">
        <fg-input :placeholder="$t('generic.status')"
                  :name="$t('generic.status')"
                  :label="$t('generic.status')"
                  class="w-full md:w-1/3 mr-2">
          <el-select v-model="resumeToEdit.status" class="select-default">
            <el-option :label="$t('resume.status.notAvailable')" value="non-available" class="select-default"/>
            <el-option :label="$t('resume.status.available')" value="available" class="select-default"/>
          </el-select>
        </fg-input>
        <fg-input :placeholder="$t('resume.travelDistance')"
                  :name="$t('resume.travelDistance')"
                  :label="$t('resume.travelDistance')"
                  v-model="resumeToEdit.travel_distance"
                  type="number"
                  class="w-full md:w-1/3 mr-2">
          <template v-slot:addonRight>
            <small>{{distanceSymbol}}</small>
          </template>
        </fg-input>
        <fg-input :label="$t('resume.open_to_relocate')" class="w-full md:w-1/3">
          <base-switch v-model="resumeToEdit.open_to_rellocate"></base-switch>
        </fg-input>
      </div>
    </base-form>
    <div class="flex w-full px-3">
      <data-table :entity="'history'"
                  :title="$t('resume.education')"
                  :data="educationHistory"
                  :columns="historyColumns"
                  :is-loading="resumeHistoryLoading"
                  operations="add"
                  :disable-permission-checks="true"
                  :go-to-add-on-click="false"
                  :go-to-details-on-row-click="false"
                  :clickable="false"
                  :searchable="false"
                  @on-add="showAddModal('education')"
                  @on-delete="deleteHistory"
                  class="w-full"
                  ref="resumeHistoryTable">
        <template v-slot:extra-buttons="{row, $index}">
          <tooltip-icon-button icon="far fa-edit"
                               :content="$t('generic.edit')"
                               @click.stop="showEditModal(row)">
          </tooltip-icon-button>
          <tooltip-icon-button :content="$t('generic.delete.title')"
                               icon="far fa-trash-alt"
                               type="danger"
                               @click.stop="deleteHistory(row, $index)">
          </tooltip-icon-button>
        </template>

        <template v-slot:type="{row}">
          {{$t(`resume.type.${row.type}`)}}
        </template>
        <template v-slot:from="{row}">
          {{$formatDate(row.from, DEFAULT_DATE_FORMAT)}}
        </template>
        <template v-slot:till="{row}">
          {{$formatDate(row.till, DEFAULT_DATE_FORMAT)}}
        </template>

      </data-table>
    </div>

    <div class="flex w-full px-3">
      <data-table :entity="'history'"
                  :title="$t('resume.type.employer')"
                  :data="employerHistory"
                  :columns="historyColumns"
                  :is-loading="resumeHistoryLoading"
                  operations="add"
                  :disable-permission-checks="true"
                  :go-to-add-on-click="false"
                  :go-to-details-on-row-click="false"
                  :clickable="false"
                  :searchable="false"
                  @on-add="showAddModal('employer')"
                  @on-delete="deleteHistory"
                  class="w-full"
                  ref="resumeHistoryTable">
        <template v-slot:extra-buttons="{row, $index}">
          <tooltip-icon-button icon="far fa-edit"
                               :content="$t('generic.edit')"
                               @click.stop="showEditModal(row)">
          </tooltip-icon-button>
          <tooltip-icon-button :content="$t('generic.delete.title')"
                               icon="far fa-trash-alt"
                               type="danger"
                               @click.stop="deleteHistory(row, $index)">
          </tooltip-icon-button>
        </template>

        <template v-slot:type="{row}">
          {{$t(`resume.type.${row.type}`)}}
        </template>
        <template v-slot:from="{row}">
          {{$formatDate(row.from, DEFAULT_DATE_FORMAT)}}
        </template>
        <template v-slot:till="{row}">
          {{$formatDate(row.till, DEFAULT_DATE_FORMAT)}}
        </template>

      </data-table>
    </div>

    <resume-history-modal :show.sync="addModalVisible"
                          :resume-id="$route.params.id"
                          :data="{type: 'employer'}"
                          @save="getHistory"
    />
    <resume-history-modal :show.sync="editModalVisible"
                          :data="historyToEdit"
                          :resume-id="$route.params.id"
                          @save="onEditSave"
    />
  </div>
</template>
<script>
  import get from 'lodash/get'
  import cloneDeep from 'lodash/cloneDeep'
  import PhoneInput from 'src/components/PhoneInput'
  import SalaryTypeSelect from 'src/components/Autocomplete/SalaryTypeSelect'
  import CountryAutocomplete from 'src/components/Autocomplete/CountryAutocomplete'
  import ResumeHistoryModal from 'src/views/admin/talent/resumes/ResumeHistoryModal'
  import TooltipIconButton from 'src/components/Buttons/TooltipIconButton'
  import { Select, Option } from 'element-ui'
  import ResumeService from 'src/services/ResumeService'
  import { DEFAULT_DATE_FORMAT } from 'src/config/dateUtils'

  export default {
    components: {
      PhoneInput,
      SalaryTypeSelect,
      CountryAutocomplete,
      ResumeHistoryModal,
      TooltipIconButton,
      [Select.name]: Select,
      [Option.name]: Option,
    },
    data () {
      return {
        loading: false,
        resumeToEdit: {},
        addModalVisible: false,
        editModalVisible: false,
        resumeHistoryLoading: false,
        historyToEdit: {},
        resumeHistory: [],
        DEFAULT_DATE_FORMAT,
      }
    },
    computed: {
      resume () {
        return this.$store.state.resume.currentResume || {}
      },
      currencySymbol () {
        return this.$store.state.config.currencySymbol
      },
      distanceSymbol() {
        return this.$store.getters['config/distanceSymbol']
      },
      modelValidations () {
        return {
          phone: {
            phoneNumber: true
          }
        }
      },
      historyColumns() {
        return [
          {
            prop: 'type',
            label: this.$t('resume.history.type'),
            minWidth: '160px',
            sortable: true
          },
          {
            prop: 'name',
            label: this.$t('resume.history.name'),
            minWidth: '160px',
            sortable: true
          },
          {
            prop: 'description',
            label: this.$t('resume.history.description'),
            minWidth: '210px',
            sortable: true
          },
          {
            prop: 'institution',
            label: this.$t('resume.history.institution'),
            minWidth: '170px',
          },
          {
            prop: 'place',
            label: this.$t('resume.history.place'),
            minWidth: '170px',
          },
          {
            prop: 'from',
            label: this.$t('resume.history.from'),
            minWidth: '120px',
            sortable: true
          },
          {
            prop: 'till',
            label: this.$t('resume.history.till'),
            minWidth: '120px',
            sortable: true
          },
        ]
      },
      employerHistory () {
        return this.resumeHistory.filter(h => h.type === 'employer')
      },
      educationHistory () {
        return this.resumeHistory.filter(h => h.type === 'education')
      },
    },
    watch: {
      resume: {
        immediate: true,
        handler (value) {
          this.resumeToEdit = cloneDeep(value)
        }
      }
    },
    methods: {
      get,
      async onFormSubmit() {
        try {
          this.loading = true
          await ResumeService.update(this.resumeToEdit.uuid, this.resumeToEdit)
          let res = await ResumeService.getById(this.resumeToEdit.uuid, {
            with: 'candidate.candidate.user'
          })
          let resume = res.data
          this.$store.commit('resume/SET_CURRENT_RESUME', resume)
        } catch (e) {
          this.$notify({type: 'danger', message: this.$t('resume.update.error')})
        } finally {
          this.loading = false
        }
      },
      async getHistory() {
        try {
          this.resumeHistoryLoading = true
          this.resumeHistory = await ResumeService.getResumeHistory(this.$route.params.id)
        } finally {
          this.resumeHistoryLoading = false
        }
      },
      showAddModal(type) {
        this.addModalVisible = true
        this.historyToEdit.type = type
      },
      showEditModal(row) {
        this.editModalVisible = true
        this.historyToEdit = row
      },
      async deleteHistory(row) {
        let msg = this.$t('generic.delete.confirmTableMessage', {
          field: 'name',
          id: row.name,
          entity: this.$t('resume.history')
        });
        await this.$confirm(msg, { dangerouslyUseHTMLString: true });
        await ResumeService.deleteResumeHistory(this.$route.params.id, row.uuid)
        const index = this.resumeHistory.findIndex(h => h.uuid === row.uuid)
        if (index !== -1) {
          this.resumeHistory.splice(index, 1)
        }
      },
      onEditSave(data) {
        const index = this.resumeHistory.findIndex(r => r.uuid === data.uuid)
        if (index !== -1) {
          this.resumeHistory.splice(index, 1, data)
        }
      }
    },
    mounted() {
      this.getHistory()
    }
  }
</script>
<style>
</style>
